<script setup>
import { ref, onMounted, computed } from 'vue';
import { useToast } from 'primevue/usetoast';
import InputText from 'primevue/inputtext';
import InputSwitch from 'primevue/inputswitch';
import Dropdown from 'primevue/dropdown';
import Button from 'primevue/button';
import Textarea from 'primevue/textarea';
import InputNumber from 'primevue/inputnumber';

import { useFetch } from '@/plugins/fetch';

const fetch = useFetch();
const toast = useToast();

const isLoading = ref(false);
const isSaving = ref(false);
const isTestingSmtp = ref(false);
const testEmailAddress = ref('');

const settings = ref({
  // General
  site_name: 'Calid',
  site_description: '',
  maintenance_mode: false,
  // Registration
  allow_registration: true,
  require_email_verification: true,
  default_user_plan: 'free',
  // Limits
  max_sensors_free: 5,
  max_sensors_basic: 25,
  max_sensors_pro: 100,
  max_sensors_enterprise: -1,
  // Data retention
  data_retention_days: 365,
  // Notifications
  smtp_enabled: false,
  smtp_host: '',
  smtp_port: 587,
  smtp_username: '',
  smtp_password: '',
  smtp_from_email: '',
  smtp_from_name: '',
  // API
  rate_limit_enabled: true,
  rate_limit_requests: 1000,
  rate_limit_window: 3600,
});

const validationErrors = ref({});

const planOptions = [
  { label: 'Free', value: 'free' },
  { label: 'Basic', value: 'basic' },
  { label: 'Pro', value: 'pro' },
  { label: 'Enterprise', value: 'enterprise' },
];

const smtpConfigValid = computed(() => {
  if (!settings.value.smtp_enabled) return true;
  return settings.value.smtp_host && 
         settings.value.smtp_port && 
         settings.value.smtp_username &&
         settings.value.smtp_from_email;
});

const validateSettings = () => {
  validationErrors.value = {};
  
  if (!settings.value.site_name?.trim()) {
    validationErrors.value.site_name = 'Site name is required';
  }
  
  if (settings.value.data_retention_days < 1 || settings.value.data_retention_days > 3650) {
    validationErrors.value.data_retention_days = 'Must be between 1 and 3650 days';
  }
  
  if (settings.value.max_sensors_free < 0) {
    validationErrors.value.max_sensors_free = 'Cannot be negative';
  }
  
  if (settings.value.smtp_enabled) {
    if (!settings.value.smtp_host?.trim()) {
      validationErrors.value.smtp_host = 'SMTP host is required';
    }
    if (!settings.value.smtp_port || settings.value.smtp_port < 1) {
      validationErrors.value.smtp_port = 'Valid port required';
    }
    if (!settings.value.smtp_from_email?.trim()) {
      validationErrors.value.smtp_from_email = 'From email is required';
    }
  }
  
  return Object.keys(validationErrors.value).length === 0;
};

const fetchSettings = async () => {
  isLoading.value = true;
  try {
    const response = await fetch('admin/settings/');
    if (response) {
      settings.value = { ...settings.value, ...response };
    }
  } catch (error) {
    console.error('Failed to fetch settings:', error);
  } finally {
    isLoading.value = false;
  }
};

const saveSettings = async () => {
  if (!validateSettings()) {
    toast.add({
      severity: 'warn',
      summary: 'Validation Error',
      detail: 'Please fix the highlighted errors',
      life: 3000,
    });
    return;
  }
  
  isSaving.value = true;
  try {
    await fetch('admin/settings/', {
      method: 'POST',
      body: settings.value,
    });
    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Settings saved successfully',
      life: 3000,
    });
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to save settings',
      life: 3000,
    });
  } finally {
    isSaving.value = false;
  }
};

const testSmtpConnection = async () => {
  if (!smtpConfigValid.value) {
    toast.add({
      severity: 'warn',
      summary: 'Configuration Incomplete',
      detail: 'Please fill in all SMTP fields before testing',
      life: 3000,
    });
    return;
  }
  
  isTestingSmtp.value = true;
  try {
    const response = await fetch('admin/settings/smtp/test', {
      method: 'POST',
      body: {
        ...settings.value,
        test_email: testEmailAddress.value || settings.value.smtp_from_email,
      },
    });
    
    if (response.success) {
      toast.add({
        severity: 'success',
        summary: 'Connection Successful',
        detail: 'SMTP test email sent successfully',
        life: 4000,
      });
    } else {
      toast.add({
        severity: 'error',
        summary: 'Connection Failed',
        detail: response.error || 'Failed to send test email',
        life: 5000,
      });
    }
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Test Failed',
      detail: error.message || 'Failed to test SMTP connection',
      life: 5000,
    });
  } finally {
    isTestingSmtp.value = false;
  }
};

const resetToDefaults = () => {
  settings.value = {
    site_name: 'Calid',
    site_description: '',
    maintenance_mode: false,
    allow_registration: true,
    require_email_verification: true,
    default_user_plan: 'free',
    max_sensors_free: 5,
    max_sensors_basic: 25,
    max_sensors_pro: 100,
    max_sensors_enterprise: -1,
    data_retention_days: 365,
    smtp_enabled: false,
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    smtp_from_email: '',
    smtp_from_name: '',
    rate_limit_enabled: true,
    rate_limit_requests: 1000,
    rate_limit_window: 3600,
  };
  validationErrors.value = {};
  toast.add({
    severity: 'info',
    summary: 'Reset',
    detail: 'Settings reset to defaults (not saved)',
    life: 3000,
  });
};

onMounted(() => {
  fetchSettings();
});
</script>

<template>
  <div class="system-settings">
    <div v-if="isLoading" class="loading-state">
      <div class="loading-spinner">
        <i class="fas fa-cog fa-spin"></i>
      </div>
      <p>Loading settings...</p>
    </div>

    <div v-else class="settings-container">
      <!-- General Settings -->
      <div class="settings-section">
        <div class="section-header">
          <div class="section-icon cyan">
            <i class="fas fa-globe"></i>
          </div>
          <div>
            <h3 class="section-title">General Settings</h3>
            <p class="section-desc">Basic site configuration</p>
          </div>
        </div>
        <div class="section-content">
          <div class="form-grid">
            <div class="form-field">
              <label>Site Name</label>
              <InputText 
                v-model="settings.site_name" 
                :class="{ 'p-invalid': validationErrors.site_name }"
              />
              <small v-if="validationErrors.site_name" class="error-text">{{ validationErrors.site_name }}</small>
            </div>

            <div class="form-field">
              <label>Default User Plan</label>
              <Dropdown
                v-model="settings.default_user_plan"
                :options="planOptions"
                optionLabel="label"
                optionValue="value"
              />
            </div>

            <div class="form-field full-width">
              <label>Site Description</label>
              <Textarea v-model="settings.site_description" rows="2" />
            </div>

            <div class="form-field">
              <div class="switch-field">
                <InputSwitch v-model="settings.maintenance_mode" />
                <div>
                  <span class="switch-label">Maintenance Mode</span>
                  <small class="switch-desc">Disables access for non-admin users</small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Registration Settings -->
      <div class="settings-section">
        <div class="section-header">
          <div class="section-icon purple">
            <i class="fas fa-user-plus"></i>
          </div>
          <div>
            <h3 class="section-title">Registration</h3>
            <p class="section-desc">User signup controls</p>
          </div>
        </div>
        <div class="section-content">
          <div class="form-grid">
            <div class="form-field">
              <div class="switch-field">
                <InputSwitch v-model="settings.allow_registration" />
                <div>
                  <span class="switch-label">Allow Registration</span>
                  <small class="switch-desc">Enable new user signups</small>
                </div>
              </div>
            </div>

            <div class="form-field">
              <div class="switch-field">
                <InputSwitch v-model="settings.require_email_verification" />
                <div>
                  <span class="switch-label">Email Verification</span>
                  <small class="switch-desc">Require email verification</small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Plan Limits -->
      <div class="settings-section">
        <div class="section-header">
          <div class="section-icon emerald">
            <i class="fas fa-layer-group"></i>
          </div>
          <div>
            <h3 class="section-title">Plan Limits</h3>
            <p class="section-desc">Maximum sensors per plan</p>
          </div>
        </div>
        <div class="section-content">
          <div class="form-grid four-cols">
            <div class="form-field">
              <label>Free Plan</label>
              <InputNumber 
                v-model="settings.max_sensors_free" 
                :min="0"
                :class="{ 'p-invalid': validationErrors.max_sensors_free }"
              />
            </div>

            <div class="form-field">
              <label>Basic Plan</label>
              <InputNumber v-model="settings.max_sensors_basic" :min="0" />
            </div>

            <div class="form-field">
              <label>Pro Plan</label>
              <InputNumber v-model="settings.max_sensors_pro" :min="0" />
            </div>

            <div class="form-field">
              <label>Enterprise</label>
              <InputNumber v-model="settings.max_sensors_enterprise" :min="-1" />
              <small class="help-text">-1 = unlimited</small>
            </div>
          </div>
          
          <div class="form-grid" style="margin-top: 1.5rem;">
            <div class="form-field">
              <label>Data Retention (days)</label>
              <InputNumber 
                v-model="settings.data_retention_days" 
                :min="1" 
                :max="3650"
                :class="{ 'p-invalid': validationErrors.data_retention_days }"
              />
              <small v-if="validationErrors.data_retention_days" class="error-text">{{ validationErrors.data_retention_days }}</small>
            </div>
          </div>
        </div>
      </div>

      <!-- Email/SMTP Settings -->
      <div class="settings-section">
        <div class="section-header">
          <div class="section-icon amber">
            <i class="fas fa-envelope"></i>
          </div>
          <div>
            <h3 class="section-title">Email Settings</h3>
            <p class="section-desc">SMTP configuration for emails</p>
          </div>
        </div>
        <div class="section-content">
          <div class="form-grid">
            <div class="form-field full-width">
              <div class="switch-field">
                <InputSwitch v-model="settings.smtp_enabled" />
                <div>
                  <span class="switch-label">Enable SMTP</span>
                  <small class="switch-desc">Send emails via SMTP server</small>
                </div>
              </div>
            </div>
          </div>

          <transition name="slide">
            <div v-if="settings.smtp_enabled" class="smtp-config">
              <div class="form-grid">
                <div class="form-field">
                  <label>SMTP Host</label>
                  <InputText 
                    v-model="settings.smtp_host" 
                    placeholder="smtp.example.com"
                    :class="{ 'p-invalid': validationErrors.smtp_host }"
                  />
                  <small v-if="validationErrors.smtp_host" class="error-text">{{ validationErrors.smtp_host }}</small>
                </div>

                <div class="form-field small">
                  <label>Port</label>
                  <InputNumber 
                    v-model="settings.smtp_port" 
                    :min="1" 
                    :max="65535"
                    :class="{ 'p-invalid': validationErrors.smtp_port }"
                  />
                </div>

                <div class="form-field">
                  <label>Username</label>
                  <InputText v-model="settings.smtp_username" />
                </div>

                <div class="form-field">
                  <label>Password</label>
                  <InputText v-model="settings.smtp_password" type="password" />
                </div>

                <div class="form-field">
                  <label>From Email</label>
                  <InputText 
                    v-model="settings.smtp_from_email" 
                    placeholder="noreply@example.com"
                    :class="{ 'p-invalid': validationErrors.smtp_from_email }"
                  />
                  <small v-if="validationErrors.smtp_from_email" class="error-text">{{ validationErrors.smtp_from_email }}</small>
                </div>

                <div class="form-field">
                  <label>From Name</label>
                  <InputText v-model="settings.smtp_from_name" placeholder="Calid Alerts" />
                </div>
              </div>

              <!-- SMTP Test -->
              <div class="smtp-test">
                <div class="test-header">
                  <i class="fas fa-flask"></i>
                  <span>Test Connection</span>
                </div>
                <div class="test-content">
                  <InputText 
                    v-model="testEmailAddress" 
                    placeholder="Test email address (optional)"
                    class="test-input"
                  />
                  <Button 
                    label="Send Test Email"
                    icon="fas fa-paper-plane"
                    :loading="isTestingSmtp"
                    :disabled="!smtpConfigValid"
                    @click="testSmtpConnection"
                    class="test-btn"
                  />
                </div>
              </div>
            </div>
          </transition>
        </div>
      </div>

      <!-- API Rate Limiting -->
      <div class="settings-section">
        <div class="section-header">
          <div class="section-icon rose">
            <i class="fas fa-tachometer-alt"></i>
          </div>
          <div>
            <h3 class="section-title">API Rate Limiting</h3>
            <p class="section-desc">Control API request limits</p>
          </div>
        </div>
        <div class="section-content">
          <div class="form-grid">
            <div class="form-field full-width">
              <div class="switch-field">
                <InputSwitch v-model="settings.rate_limit_enabled" />
                <div>
                  <span class="switch-label">Enable Rate Limiting</span>
                  <small class="switch-desc">Limit API requests per user</small>
                </div>
              </div>
            </div>

            <template v-if="settings.rate_limit_enabled">
              <div class="form-field">
                <label>Max Requests</label>
                <InputNumber v-model="settings.rate_limit_requests" :min="1" />
              </div>

              <div class="form-field">
                <label>Time Window (seconds)</label>
                <InputNumber v-model="settings.rate_limit_window" :min="1" />
                <small class="help-text">{{ Math.round(settings.rate_limit_window / 60) }} minutes</small>
              </div>
            </template>
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="settings-actions">
        <Button
          label="Reset to Defaults"
          icon="fas fa-undo"
          class="reset-btn"
          @click="resetToDefaults"
          outlined
        />
        <Button
          label="Save All Settings"
          icon="fas fa-check"
          class="save-btn"
          @click="saveSettings"
          :loading="isSaving"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
.system-settings {
  padding: 0.5rem;
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  color: #6b6b7b;
}

.loading-spinner {
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 212, 255, 0.1);
  border-radius: 50%;
  margin-bottom: 1rem;
}

.loading-spinner i {
  font-size: 1.5rem;
  color: #00d4ff;
}

/* Settings Container */
.settings-container {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

/* Settings Section */
.settings-section {
  background: rgba(20, 20, 30, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  overflow: hidden;
  animation: sectionAppear 0.4s ease;
}

@keyframes sectionAppear {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.section-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  background: rgba(255, 255, 255, 0.02);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.section-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  color: white;
}

.section-icon.cyan { background: linear-gradient(135deg, #00d4ff, #0ea5e9); }
.section-icon.purple { background: linear-gradient(135deg, #7c3aed, #a855f7); }
.section-icon.emerald { background: linear-gradient(135deg, #10b981, #34d399); }
.section-icon.amber { background: linear-gradient(135deg, #f59e0b, #fbbf24); }
.section-icon.rose { background: linear-gradient(135deg, #f43f5e, #fb7185); }

.section-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #f0f0f5;
  margin: 0;
}

.section-desc {
  font-size: 0.85rem;
  color: #6b6b7b;
  margin: 0;
}

.section-content {
  padding: 1.5rem;
}

/* Form Grid */
.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.25rem;
}

.form-grid.four-cols {
  grid-template-columns: repeat(4, 1fr);
}

@media (max-width: 768px) {
  .form-grid, .form-grid.four-cols {
    grid-template-columns: 1fr;
  }
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-field.full-width {
  grid-column: 1 / -1;
}

.form-field.small {
  max-width: 120px;
}

.form-field label {
  font-size: 0.85rem;
  font-weight: 500;
  color: #a0a0b0;
}

/* Override PrimeVue input styles */
:deep(.p-inputtext),
:deep(.p-dropdown),
:deep(.p-inputnumber-input),
:deep(.p-textarea) {
  background: rgba(30, 30, 42, 0.8) !important;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  color: #f0f0f5 !important;
  border-radius: 10px !important;
}

:deep(.p-inputtext:focus),
:deep(.p-dropdown:focus),
:deep(.p-inputnumber-input:focus),
:deep(.p-textarea:focus) {
  border-color: #00d4ff !important;
  box-shadow: 0 0 0 3px rgba(0, 212, 255, 0.15) !important;
}

:deep(.p-invalid) {
  border-color: #ef4444 !important;
}

.help-text {
  font-size: 0.75rem;
  color: #6b6b7b;
}

.error-text {
  font-size: 0.75rem;
  color: #f87171;
}

/* Switch Field */
.switch-field {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
}

.switch-label {
  display: block;
  font-weight: 500;
  color: #f0f0f5;
  margin-bottom: 0.125rem;
}

.switch-desc {
  display: block;
  font-size: 0.8rem;
  color: #6b6b7b;
}

/* SMTP Config Animation */
.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease;
}

.slide-enter-from,
.slide-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.smtp-config {
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

/* SMTP Test */
.smtp-test {
  margin-top: 1.5rem;
  background: rgba(245, 158, 11, 0.05);
  border: 1px solid rgba(245, 158, 11, 0.2);
  border-radius: 12px;
  overflow: hidden;
}

.test-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  background: rgba(245, 158, 11, 0.1);
  color: #fbbf24;
  font-weight: 500;
  font-size: 0.9rem;
}

.test-content {
  display: flex;
  gap: 1rem;
  padding: 1rem;
  flex-wrap: wrap;
}

.test-input {
  flex: 1;
  min-width: 200px;
}

.test-btn {
  background: linear-gradient(135deg, #f59e0b, #d97706) !important;
  border: none !important;
}

/* Actions */
.settings-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.reset-btn {
  color: #6b6b7b !important;
  border-color: rgba(255, 255, 255, 0.15) !important;
}

.reset-btn:hover {
  color: #f0f0f5 !important;
  border-color: rgba(255, 255, 255, 0.3) !important;
}

.save-btn {
  background: linear-gradient(135deg, #10b981, #059669) !important;
  border: none !important;
  padding: 0.75rem 2rem !important;
}

.save-btn:hover {
  box-shadow: 0 8px 25px rgba(16, 185, 129, 0.3);
}
</style>
